import { FormSectionLayout, useComputed } from "@homebound/beam";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BaseChangeEventFormState } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { ReviewStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReviewStep";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { addEntityParam } from "src/RouteUrls";
import { fail, queriesResult } from "src/utils";
import {
  useReviewImpactProjectQuery,
  useReviewProjectImpactCheckoutConfigQuery,
} from "../3.ReviewImpactStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { resolveCheckoutConfigFields } from "../reviewImpactUtils";
import { ApplyConfigurationSummaryStats } from "./ApplyConfigurationSummaryStats";

type ApplyConfigurationReviewStepProps = { formState: WorkflowFormState<ApplyConfigurationFormInput> };

/**
 * The wizard's final step, which is two different reviews.
 *
 * With a signed contract step 1 already saved a draft change event, so this is the shared review-and-submit form and
 * Create sends the draft for approval. Without one nothing has been saved and Apply writes the configuration straight
 * onto the project, so there is no change event to title, attach a contract to, or route for approval — only a
 * summary of what Apply is about to do.
 */
export function ApplyConfigurationReviewStep(props: ApplyConfigurationReviewStepProps) {
  const { formState } = props;
  const { idOrAdd } = useRouteParams<GuidedChangeEventParams>();
  const hasChangeEvent = idOrAdd !== addEntityParam;
  // Not on `BaseChangeEventFormInput`, so it's read here rather than inside the shared step.
  const checkoutConfigId = useComputed(() => formState.checkoutConfigId.value, [formState]);

  if (hasChangeEvent) {
    return <ReviewStep formState={formState as BaseChangeEventFormState<ApplyConfigurationFormInput>} />;
  } else {
    return (
      <ReviewDirectApply formState={formState} checkoutConfigId={checkoutConfigId ?? fail("No checkout config id")} />
    );
  }
}

type ReviewDirectApplyProps = { formState: WorkflowFormState<ApplyConfigurationFormInput>; checkoutConfigId: string };

/** Re-runs the Review Impact step's queries, which Apollo serves from cache, so this is a summary not a second load. */
function ReviewDirectApply(props: ReviewDirectApplyProps) {
  const { formState, checkoutConfigId } = props;
  const { projectId } = useRouteParams<GuidedChangeEventParams>();

  const projectQuery = useReviewImpactProjectQuery({ variables: { projectId } });
  const checkoutConfigQuery = useReviewProjectImpactCheckoutConfigQuery({
    variables: { structureId: projectId, configId: checkoutConfigId },
  });

  return queriesResult([projectQuery, checkoutConfigQuery] as const, {
    data: ({ project }, { structure, checkoutConfig }) => (
      <FormSectionLayout
        title="Ready to Apply"
        description="Review the summary of changes below. This project does not have a signed contract, so the configuration will be applied immediately to the project, and no Change Event will be created."
        initialFields={
          <ApplyConfigurationSummaryStats
            formState={formState}
            clientNoun={project.lotType.clientNoun}
            projectCostCodes={project.projectCostCodes}
            scheduleImpactVariables={{ projectId, checkoutConfigId }}
            {...resolveCheckoutConfigFields(checkoutConfig, structure.isAdu)}
          />
        }
        sections={[]}
      />
    ),
  });
}
